import React, { useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import { useStores } from "_common/hooks";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NotificationManager } from "react-notifications";
import { PaymentApi } from "states/api/payment";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatBalance, formatNumber } from "_common/utils/formatValue";
import i18next, { use } from "i18next";
import HeaderTop from "_common/component/Layout/Header/HeaderTop";
import { UserWalletApi } from "states/api/wallet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import SelectBox from "_common/component/SelectBox";
import ModalCountry from "./ModalCountry";

export default function WalletAddress() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [wallet_type, setWalletType] = useState("bank");
  const [back_images, setBackImage] = useState();
  const [openModal, setOpenModal] = useState(false);

  const schema = yup.object({}).required();

  const {
    authStore: { user, general, user_wallet, game_wallet, coins },
  } = useStores();

  const {
    register,
    trigger,
    handleSubmit,
    formState: { errors, isValid, isSubmitted },
    getValues,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const { mutate: onSubmit, isLoading } = useMutation(
    (variables) =>
      UserWalletApi.create({
        params: {
          ...variables,
          image: back_images,
        },
      }),
    {
      onSuccess: (res) => {
        reset();
        setBackImage();
        queryClient.invalidateQueries(["user_wallet", "list"]);
        navigate(-1);
        NotificationManager.success(
          i18next.t(res?.msg) || i18next.t("add_success")
        );
      },
      onError: (error) => {
        const errorMessage =
          i18next.t(error?.message) ?? i18next.t("action_failed_msg");
        NotificationManager.error(errorMessage);
      },
    }
  );

  const onSave = (values) => {
    if (isLoading) return null;
    onSubmit({ ...values, type: wallet_type });
    return null;
  };

  const onReceiveBackImages = (images) => {
    setBackImage(images[0]?.url);
  };

  const handleChangeCountry = (item) => {
    setValue("country", item?.name);
  };

  const CurrencyBody = useMemo(
    () => (
      <>
        <div className="sbui-input-container">
          <select
            className="sbui-select sbui-select--medium"
            {...register("symbol")}
          >
            <option value="usdt">USDT - ERC20</option>
          </select>
          <fieldset className="label-field">
            <legend className="text-[#888888]">
              <span>{i18next.t("symbol")}</span>
            </legend>
          </fieldset>
        </div>
        <div className="sbui-input-container">
          <input
            className="sbui-input sbui-input--medium"
            placeholder={i18next.t("wallet_address")}
            {...register("address")}
          />
          <fieldset className="label-field">
            <legend className="text-[#888888]">
              <span>{i18next.t("wallet_address")}</span>
            </legend>
          </fieldset>
        </div>
      </>
    ),
    []
  );

  const BankBody = useMemo(
    () => (
      <>
        <div className="sbui-input-container">
          <input
            className="sbui-input sbui-input--medium"
            placeholder={i18next.t("country")}
            readOnly
            onClick={() => setOpenModal(true)}
            {...register("country")}
          />
          <FontAwesomeIcon
            icon={faArrowRight}
            fontSize={20}
            className="text-[#9098A3] absolute right-[15px] top-[18px]"
          />
          <fieldset className="label-field">
            <legend className="text-[#888888]">
              <span>{i18next.t("country")}</span>
            </legend>
          </fieldset>
        </div>
        <div className="sbui-input-container">
          <input
            className="sbui-input sbui-input--medium"
            placeholder={i18next.t("bank")}
            {...register("bank_name")}
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            onCopy={(e) => {
              e.preventDefault();
              return false;
            }}
          />
          <fieldset className="label-field">
            <legend className="text-[#888888]">
              <span>{i18next.t("bank")}</span>
            </legend>
          </fieldset>
        </div>
        <div className="sbui-input-container">
          <input
            className="sbui-input sbui-input--medium"
            placeholder={i18next.t("bank_number")}
            {...register("bank_number")}
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            onCopy={(e) => {
              e.preventDefault();
              return false;
            }}
          />
          <fieldset className="label-field">
            <legend className="text-[#888888]">
              <span>{i18next.t("bank_number")}</span>
            </legend>
          </fieldset>
        </div>
        <div className="sbui-input-container">
          <input
            className="sbui-input sbui-input--medium"
            placeholder={i18next.t("real_name")}
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            onCopy={(e) => {
              e.preventDefault();
              return false;
            }}
            {...register("bank_owner")}
          />
          <fieldset className="label-field">
            <legend className="text-[#888888]">
              <span>{i18next.t("real_name")}</span>
            </legend>
          </fieldset>
        </div>
        <div className="sbui-input-container">
          <input
            className="sbui-input sbui-input--medium"
            placeholder={i18next.t("address_contract")}
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            onCopy={(e) => {
              e.preventDefault();
              return false;
            }}
            {...register("address_contract")}
          />
          <fieldset className="label-field">
            <legend className="text-[#888888]">
              <span>{i18next.t("address_contract")}</span>
            </legend>
          </fieldset>
        </div>
        <div className="sbui-input-container">
          <input
            className="sbui-input sbui-input--medium"
            placeholder={i18next.t("phone_contract")}
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            onCopy={(e) => {
              e.preventDefault();
              return false;
            }}
            {...register("phone")}
          />
          <fieldset className="label-field">
            <legend className="text-[#888888]">
              <span>{i18next.t("phone_contract")}</span>
            </legend>
          </fieldset>
        </div>
        <div className="sbui-input-container">
          <input
            className="sbui-input sbui-input--medium"
            placeholder={i18next.t("card_id")}
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            onCopy={(e) => {
              e.preventDefault();
              return false;
            }}
            {...register("card_id")}
          />
          <fieldset className="label-field">
            <legend className="text-[#888888]">
              <span>{i18next.t("card_id")}</span>
            </legend>
          </fieldset>
        </div>
      </>
    ),
    []
  );

  return (
    <div className="h-screen overflow-auto bg-[#000000] pt-[60px]">
      <HeaderTop title={i18next.t("add_wallet_address")} />
      <div className="p-4 my-2 flex items-center justify-center">
        <img
          className="w-[200px] h-[200px]"
          src="https://tradingm68.com/assets/images/create-account-withdraw.svg"
          alt=""
        />
      </div>
      <div id="withdraw">
        <form className="sbui-card-content" onSubmit={handleSubmit(onSave)}>
          <div className="flex flex-col w-full gap-6">
            <SelectBox
              label={i18next.t("type")}
              onChange={(item) => setWalletType(item?.value)}
              options={[
                {
                  value: "bank",
                  label: i18next.t("note4"),
                },
                {
                  value: "currency",
                  label: i18next.t("crypto"),
                },
              ]}
            />
            {wallet_type === "bank" && BankBody}
            {wallet_type === "currency" && CurrencyBody}
            <div className="sbui-input-container">
              <input
                className="sbui-input sbui-input--medium"
                placeholder={i18next.t("note_wallet_address")}
                {...register("note")}
              />
              <fieldset className="label-field">
                <legend className="text-[#888888]">
                  <span>{i18next.t("note_wallet_address")}</span>
                </legend>
              </fieldset>
            </div>
            <button
              type="submit"
              className="p-2 mt-2 flex items-center justify-center text-base font-medium text-[#fff] rounded bg-[#3D5AFE] hover:bg-[#2a3eb1]"
            >
              <span className="uppercase">{i18next.t("confirm")}</span>
            </button>
          </div>
        </form>
      </div>
      {openModal && (
        <ModalCountry
          onClose={() => setOpenModal(false)}
          onChange={handleChangeCountry}
        />
      )}
    </div>
  );
}
